import { FC } from 'react'
import { ReactComponent as BackIcon } from 'src/assets/svg/arrow-left-s-line.svg'
import { cn } from 'src/utils/cn'
import { CampaignDetailView } from 'src/pages/ThemeDetail/CampaignDetailHeader'
import styles from './reviewAnalysisHeader.module.scss'

// What a screen can ask of the header when it renders one for itself.
export interface ReviewHeaderOptions {
  embedded?: boolean
  onBack?: () => void
  trail?: string
}

interface ReviewAnalysisHeaderProps {
  // Inside a card rather than on the page background: it fills the card's own
  // width instead of centring a 1170px column of its own.
  embedded?: boolean
  // Where the back arrow goes. Passing it turns the header into the trail the
  // comparison screens use — a way back, not a page title — since on those the
  // heading belongs to the report under it.
  onBack?: () => void
  onViewChange: (view: CampaignDetailView) => void
  // What the trail names after the page, e.g. "Data comparisons".
  trail?: string
  view: CampaignDetailView
}

// The switch reads "Overview / Comparisons" on every feature that carries it,
// so the two are recognisably the same control rather than two similar ones.
const COPY = {
  comparison: 'Comparisons',
  general: 'Overview',
  title: 'Review Analysis',
  trailTitle: 'Review Analysis',
}

const VIEWS: { label: string; value: CampaignDetailView }[] = [
  { label: COPY.general, value: 'general' },
  { label: COPY.comparison, value: 'comparison' },
]

// The page's own title and the switcher between its two views. Social
// Listening's header does the same job, but it carries a campaign's name, date
// and actions — this page has one title and one switch, so it is its own
// component rather than that one bent around a second use.
export const ReviewAnalysisHeader: FC<ReviewAnalysisHeaderProps> = ({
  embedded = false,
  onBack,
  onViewChange,
  trail,
  view,
}) => (
  <div className={cn(styles.header, embedded && styles.headerEmbedded)}>
    {onBack ? (
      <button className={styles.back} onClick={onBack} type="button">
        <BackIcon className={styles.backIcon} />
        <span className={styles.backLabel}>
          {COPY.trailTitle}
          {trail && <span className={styles.trail}> · {trail}</span>}
        </span>
      </button>
    ) : (
      <h1 className={styles.title}>{COPY.title}</h1>
    )}

    <div className={styles.viewToggle}>
      {VIEWS.map((option) => (
        <button
          className={cn(
            styles.viewToggleOption,
            view === option.value && styles.viewToggleOptionActive
          )}
          key={option.value}
          onClick={() => onViewChange(option.value)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
)
